/**
 * O ponto ÚNICO de seleção de transporte de voz (`contracts/transporte.md`)
 * — [T021]. Ninguém fora daqui decide entre `porTurnos`/`tempoReal`: nem a
 * fachada (`motor-de-voz.js`, que só chama `selecionarTransporte(...)` e
 * delega), nem componente de tela algum (spec US-3 AC3).
 *
 * ## Por que o ponto de seleção tem que ser ÚNICO (invariante N4)
 *
 * N4 (`contracts/transporte.md`): "VAD local e juiz do provedor decidindo o
 * MESMO turno ao mesmo tempo" nunca pode acontecer. Isso só é garantido se
 * existir UM lugar só decidindo qual transporte está no comando — dois
 * pontos de seleção (um na fachada, outro num componente, por exemplo)
 * poderiam divergir sob uma corrida (a queda do [T034] acontece no meio de
 * uma sessão: um decide `porTurnos` e o outro `tempoReal` pro MESMO turno) e aí os dois
 * juízes — o VAD local do `porTurnos` E o provedor do `tempoReal` — rodariam
 * em paralelo. Centralizar aqui é o que torna N4 verificável: um teste
 * chamando SÓ esta função cobre toda decisão do sistema.
 *
 * `MODO_DITAR` é SEMPRE `porTurnos` — ditar é pontual por natureza (spec
 * §5): não há conversa contínua a manter, então o transporte em tempo real
 * (pensado pra diálogo com barge-in do provedor) não se aplica.
 *
 * ## `tempoReal` é um PONTO DE EXTENSÃO real, não um `if` que finge decidir
 *
 * Quem implementa `tempoReal` é `transporte-tempo-real.js` ([T030]). Em vez
 * de um desvio hardcoded que sempre cai no mesmo lugar (o que tornaria
 * QUALQUER teste do roteamento vacuamente verde — não haveria como provar que
 * o `MODO_VOZ` de fato troca de transporte, só que ele não quebra nada), o
 * desvio é um REGISTRO: `registrarTransporteTempoReal(fabrica)`.
 * `transporte-tempo-real.js` chama isso uma vez, na carga dele — que por sua
 * vez só acontece porque `motor-de-voz.js` o importa ([T030b]). Sem nada
 * registrado, `_fabricaTempoReal` é `null` e o `MODO_VOZ` cai em `porTurnos`
 * de qualquer jeito. O registro também é o que deixa ESTE arquivo testável
 * fim-a-fim com uma fábrica FALSA no teste (ver `transporte-de-voz.unit.js`,
 * "roteamento com tempoReal registrado").
 *
 * ## ★ [T046] O tempo real é o PADRÃO — não há bandeira a consultar (D-O-4)
 *
 * Até aqui a decisão do `MODO_VOZ` era uma bandeira de rollout
 * (`bandeiraTempoRealLigada`, do `FEATURES.IA_VOZ_TEMPO_REAL` no `$access`).
 * **Ela saiu**: em Modo de Voz quem atende é o transporte em tempo real.
 *
 * O que sobrou NÃO é bandeira disfarçada — são dois fatos do turno, e cada um
 * tem motivo próprio:
 *
 * | fato | por que ainda decide |
 * |---|---|
 * | `modo === MODO_DITAR` | ditar é pontual por natureza (spec §5) — é DESENHO, e a D-O-4 o mantém explicitamente |
 * | `forcarPorTurnos` | a queda do [T034] reabrindo o v1 na MESMA sessão; sem ele, a reabertura reescolheria o canal que acabou de cair, em laço |
 * | `superficie` vazia | `abrir` RECUSA sessão sem superfície (`MOTIVO_SEM_SUPERFICIE`): mandar ao tempo real um composer que não a declara gasta uma queda e uma linha de aviso por clique no microfone, para terminar no MESMO `porTurnos` |
 *
 * A última linha é a que mais parece rollout e não é: o popup, o painel do
 * Hub, o Fee-Based e o ai-framework montam o MESMO composer, e nenhum deles
 * declara superfície. Ligar o tempo real por lá não é uma decisão de bandeira
 * — é chamar um endpoint que já se sabe que vai recusar.
 */
import { criarTransportePorTurnos } from './transporte-por-turnos'
import { MODO_DITAR } from './captura-de-voz'
import { TRANSPORTE_POR_TURNOS, TRANSPORTE_TEMPO_REAL } from './medicao-de-voz'
import { definirTransporteNoComando } from './transporte-no-comando'
import { marcarFalhaDaMedicao } from './instrumentacao-de-medicao'

export { TRANSPORTE_POR_TURNOS, TRANSPORTE_TEMPO_REAL }

// Criado AQUI, na carga do módulo — mesmo instante em que `motor-de-voz.js`
// fazia os próprios `$watch`/registros hoje (eager, module-level). Continua
// sendo a MESMA instância pelo tempo de vida do módulo: é o singleton que
// `motor-de-voz.js` sempre foi, só que agora atrás de uma factory nomeada
// (ver o cabeçalho de `transporte-por-turnos.js`).
const _porTurnos = criarTransportePorTurnos()

let _fabricaTempoReal = null
let _tempoReal = null

/**
 * [T030] Ponto de registro do transporte `tempoReal` — chamado UMA VEZ pelo
 * módulo que o implementa (nunca por um componente de tela; a fachada
 * também não chama isto). Fábrica, não instância: mantém a MESMA regra de
 * `criarTransportePorTurnos` — o transporte só é instanciado quando de fato
 * selecionado (memoizado a partir daí), nunca antes.
 *
 * @param {() => Object} fabrica `() => TransporteDeVoz`, ou `null` para
 *   desregistrar (uso de teste — chamar de novo com `null` limpa o registro
 *   e a instância memoizada).
 */
export const registrarTransporteTempoReal = (fabrica) => {
  _fabricaTempoReal = typeof fabrica === 'function' ? fabrica : null
  _tempoReal = null
}

const _obterTempoReal = () => {
  if (!_fabricaTempoReal) return null
  if (!_tempoReal) _tempoReal = _fabricaTempoReal()
  return _tempoReal
}

/**
 * @param {Object} opts
 * @param {string} opts.modo `MODO_DITAR` | `MODO_VOZ` (`./captura-de-voz`).
 * @param {string} [opts.superficie] a superfície que a TELA declara
 *   (`ia_operacional`, …) — **não é bandeira**, é o campo que `abrir` EXIGE
 *   (`MOTIVO_SEM_SUPERFICIE`). Só é consultada para `MODO_VOZ`.
 * @param {boolean} [opts.forcarPorTurnos] a queda do [T034]: o v1 reabrindo na
 *   MESMA sessão, sem reescolher o canal que acabou de cair.
 * @returns {Object} o transporte selecionado, no formato `TransporteDeVoz`
 *   (`contracts/transporte.md`) — `tempoReal` no Modo de Voz de uma superfície
 *   declarada; `porTurnos` no Ditar, na queda, sem superfície ou sem
 *   `tempoReal` registrado.
 */
export const selecionarTransporte = ({ modo, superficie, forcarPorTurnos = false } = {}) => {
  if (modo === MODO_DITAR) return _porTurnos
  if (forcarPorTurnos) return _porTurnos
  if (!String(superficie || '').trim()) return _porTurnos
  const tempoReal = _obterTempoReal()
  return tempoReal || _porTurnos
}

/**
 * [T034] O motivo gravado quando a abertura falhou sem NOMEAR causa — erro
 * que não é `ErroDeVozTempoReal` (um `TypeError` de programação, por
 * exemplo). Motivo nomeado é exigência do N10: linha de medição sem motivo
 * não diz nada, e motivo chutado diz coisa errada.
 */
export const MOTIVO_ABERTURA_TEMPO_REAL_FALHOU = 'abertura_tempo_real_falhou'

/**
 * [T034] A UMA linha que o consultor vê quando a sessão em tempo real não
 * abriu. Diz o que aconteceu E que ele pode continuar falando — a mensagem
 * sem a segunda metade deixaria a pessoa achando que a voz morreu. Não
 * carrega motivo técnico: o motivo vai para a medição, não para a tela
 * (N7 — nunca detalhe de erro cru na cara do consultor).
 */
export const MENSAGEM_QUEDA_PARA_POR_TURNOS =
  'A voz em tempo real não está disponível agora. Seguimos pelo modo de voz de sempre.'

const _avisarQueda = (ctx) => {
  const avisos = ctx.avisos || {}
  if (typeof avisos.avisarErro !== 'function') return
  avisos.avisarErro(MENSAGEM_QUEDA_PARA_POR_TURNOS)
}

/**
 * ★ [T034] ABRE a sessão pelo transporte selecionado e, se o de tempo real
 * não abrir, **cai para o caminho por turnos na MESMA sessão do consultor**
 * (US-3 AC5) — avisando em uma linha e registrando o motivo.
 *
 * ## Por que abrir mora AQUI, e não na fachada
 *
 * Quem escolhe é este módulo (invariante N4: ponto ÚNICO de seleção). A
 * queda é uma RE-escolha — decidir, no meio da abertura, que quem manda
 * passou a ser o outro transporte. Deixá-la na fachada criaria um segundo
 * lugar decidindo transporte, que é exatamente o que o N4 proíbe; e é aqui
 * que está a única referência ao `_porTurnos` sem passar por seleção nova.
 *
 * ## "Na mesma sessão" quer dizer: o consultor não recomeça nada
 *
 * Não há reinício de tela, de conversa nem de Modo de Voz — o `abrir` do
 * caminho por turnos recebe o MESMO `ctx` (mesmo `modo`, mesmos `avisos`) e
 * o consultor segue falando. O microfone que o tempo real chegou a abrir já
 * foi solto pelo próprio `_desmontar` dele antes de rejeitar, então não
 * ficam dois capturando.
 *
 * ## O registro é a linha de MEDIÇÃO, não um `console`
 *
 * `marcarFalhaDaMedicao(motivo)` é o canal nomeado do módulo (N10: turno que
 * falhou nunca some da medição, e o motivo é nomeado). `console.warn` não
 * serve: além de não sobreviver ao usuário real, o `tests/unit/setup.js`
 * deste repo faz `console.warn`/`console.error` REPROVAREM o teste — de
 * propósito.
 *
 * ⚠️ **Toda falha cai, inclusive o microfone negado** — e aí o consultor vê
 * DUAS linhas: esta e a do próprio caminho por turnos ao pedir o microfone
 * de novo e ser negado outra vez. Foi escolhido não abrir exceção por
 * motivo: a regra "falhou, cai" é a que se consegue provar inteira, e a
 * segunda mensagem é a ACIONÁVEL (é ela que fala em microfone). Fica
 * declarado em vez de escondido.
 *
 * @param {Object} ctx `ContextoDeSessao` + `{modo, avisos}` — repassado
 *   INTEIRO aos dois transportes; cada um lê o que entende.
 * @returns {Promise<{transporte: Object, caiu: boolean, motivo: string|null}>}
 *   quem ficou com a sessão, se houve queda e por quê.
 */
/**
 * ★ [T050] Entrega o destino da TELA a quem acabou de assumir o microfone.
 *
 * Sem isto o transporte que assume nasce **sem destino**, porque a tela
 * registra o dela no `mounted()` — antes de existir sessão, quando o
 * `transporteNoComando()` ainda é o `porTurnos`. O `emitir` de todo
 * transporte é `if (!_destino …) return`, então a sessão seguia PAGA e MUDA:
 * `audioRemoto` sem `<audio>`, `transcricaoParcial` sem tela e — o que
 * transformou um defeito de minutos numa investigação de horas — `erro` e
 * `queda` sem uma linha sequer.
 *
 * Idempotente e sem opinião: registrar o mesmo destino duas vezes é
 * atribuição, não acúmulo. Ctx sem destino (teste, Ditar por API) não mexe em
 * nada — quem já tinha destino continua com ele.
 */
const _entregarDestino = (transporte, ctx) => {
  if (!ctx.destino) return
  transporte.registrarDestino(ctx.destino)
}

/**
 * ★ [T034] A QUEDA: avisa em uma linha, registra o motivo NOMEADO e reabre o
 * caminho por turnos na MESMA sessão do consultor. Extraída de
 * `abrirTransporte` só para caber no `max-statements` — é o corpo do `catch`,
 * byte a byte.
 */
const _cairParaPorTurnos = async (ctx, erro) => {
  const motivo = (erro && erro.motivo) || MOTIVO_ABERTURA_TEMPO_REAL_FALHOU
  marcarFalhaDaMedicao(motivo)
  _avisarQueda(ctx)
  definirTransporteNoComando(_porTurnos)
  _entregarDestino(_porTurnos, ctx)
  await _porTurnos.abrir(ctx)
  return { transporte: _porTurnos, caiu: true, motivo }
}

export const abrirTransporte = async (ctx = {}) => {
  const escolhido = selecionarTransporte(ctx)
  // O comando é declarado ANTES do `abrir`: o caminho por turnos escreve
  // `sessaoDeVozAtiva` DENTRO dele, e o `$watch` que reage precisa já ver
  // quem manda (ver `transporte-no-comando.js`).
  definirTransporteNoComando(escolhido)
  // ANTES do `abrir`, e não depois: o data channel entrega evento assim que a
  // negociação fecha, e `session.started` é o primeiro deles.
  _entregarDestino(escolhido, ctx)
  if (escolhido === _porTurnos) {
    await _porTurnos.abrir(ctx)
    return { transporte: _porTurnos, caiu: false, motivo: null }
  }
  try {
    await escolhido.abrir(ctx)
    return { transporte: escolhido, caiu: false, motivo: null }
  } catch (erro) {
    return _cairParaPorTurnos(ctx, erro)
  }
}
